import { classNames } from '@commenda/utils';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Fragment, useEffect, useState } from 'react';
import { HiCheck } from 'react-icons/hi';

interface ISelectInput {
  options: {
    displayedValue: string;
    value: unknown;
  }[];
  value: unknown;
  setValue?: (arg0) => unknown;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const SelectInput = <T extends number | string>({
  options,
  value,
  setValue,
  placeholder = '---',
  disabled = false,
  className,
}: ISelectInput) => {
  const [displayedValue, setDisplayedValue] = useState(placeholder);
  useEffect(() => {
    if (options?.map((option) => option?.value).includes(value)) {
      setDisplayedValue(
        options?.find((option) => option?.value === value).displayedValue
      );
    } else {
      setDisplayedValue(placeholder);
    }
  }, [value, options, placeholder]);

  return (
    <Listbox
      value={value}
      onChange={(val: T) => setValue(val)}
      disabled={disabled}
    >
      {({ open }) => (
        <div className={classNames('relative w-full', className)}>
          <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100">
            <span
              className={`block truncate ${displayedValue ?? 'text-gray-400'}`}
            >
              {displayedValue || placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon
                className="h-4 w-4 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="border absolute z-50 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm w-full">
              {options?.map((option) => (
                <Listbox.Option
                  key={option.displayedValue}
                  className={({ active }) =>
                    classNames(
                      active ? 'text-white bg-blue-500' : 'text-gray-900',
                      'relative cursor-default select-none py-2 pl-3 pr-9'
                    )
                  }
                  value={option?.value}
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={classNames(
                          selected ? 'font-semibold' : 'font-normal',
                          'block truncate'
                        )}
                      >
                        {option?.displayedValue}
                      </span>

                      {selected ? (
                        <span
                          className={classNames(
                            active ? 'text-white' : 'text-blue-600',
                            'absolute inset-y-0 right-0 flex items-center pr-4'
                          )}
                        >
                          <HiCheck className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};

export default SelectInput;
